import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";
import { getAccessToken } from "src/helper/storage";
import useAppStore from "src/store/utils";
import {
  Currency,
  EventOwnerRole,
  UpdateEventPreferenceMutationVariables,
} from "src/types/api.d";

interface AppContextProps {
  children: JSX.Element;
}

export interface ValueProps {
  acceptTeamToken: string | null;
  acceptTeamPhone: string | null;
  userId: number | null;
  setUserId: Dispatch<SetStateAction<number | null>>;
  token: string | null;
  setToken: Dispatch<SetStateAction<string | null>>;
  setWalletCode: Dispatch<SetStateAction<string | null | undefined>>;
  setAcceptTeamToken: Dispatch<SetStateAction<string | null>>;
  setAcceptTeamPhone: Dispatch<SetStateAction<string | null>>;
  walletCode: string | null | undefined;
  isAdmin: boolean;
  setIsAdmin: Dispatch<SetStateAction<boolean>>;
  userRole: EventOwnerRole | null;
  setUserRole: Dispatch<SetStateAction<EventOwnerRole | null>>;
  isLuxeEvent: boolean;
  setIsLuxeEvent: Dispatch<SetStateAction<boolean>>;
  isLegacyEvent: boolean;
  setIsLegacyEvent: Dispatch<SetStateAction<boolean>>;
  eventPreferences: UpdateEventPreferenceMutationVariables["input"];
  setEventPreferences: Dispatch<
    SetStateAction<UpdateEventPreferenceMutationVariables["input"]>
  >;
  eventName: string;
  setEventName: Dispatch<SetStateAction<string>>;
  isPaymentSetup: Map<Currency, boolean> | null;
  setIsPaymentSetup: Dispatch<SetStateAction<Map<Currency, boolean> | null>>;
  isPaymentSetupLoading: boolean;
  setIsPaymentSetupLoading: Dispatch<SetStateAction<boolean>>;
  addTeam: boolean;
  setAddTeam: Dispatch<SetStateAction<boolean>>;
  asoebiFeePercentage: number;
  setAsoebiFeePercentage: Dispatch<SetStateAction<number>>;
}
export const AppContext = createContext<ValueProps | null>(null);

const ACCEPT_TEAM_TOKEN_SESSION_KEY = "acceptTeamToken";
const ACCEPT_TEAM_PHONE_SESSION_KEY = "acceptTeamPhone";

const AppContextProvider: React.FC<AppContextProps> = ({ children }) => {
  const [addTeam, setAddTeam] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>("");
  const [userId, setUserId] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<EventOwnerRole | null>(() => {
    const storedRole = localStorage.getItem("userRole");
    return storedRole ? (storedRole as EventOwnerRole) : null;
  });
  const [isLuxeEvent, setIsLuxeEvent] = useState<boolean>(false);
  const [isLegacyEvent, setIsLegacyEvent] = useState<boolean>(false);
  const [acceptTeamToken, setAcceptTeamToken] = useState<string | null>(() =>
    sessionStorage.getItem(ACCEPT_TEAM_TOKEN_SESSION_KEY)
  );
  const [acceptTeamPhone, setAcceptTeamPhone] = useState<string | null>(() =>
    sessionStorage.getItem(ACCEPT_TEAM_PHONE_SESSION_KEY)
  );
  const [eventName, setEventName] = useState("");
  const [walletCode, setWalletCode] = useState<string | null | undefined>("");
  const { event } = useAppStore((state) => state);
  const [isPaymentSetup, setIsPaymentSetup] = useState<Map<
    Currency,
    boolean
  > | null>(null);
  const [isPaymentSetupLoading, setIsPaymentSetupLoading] = useState(true);
  const [asoebiFeePercentage, setAsoebiFeePercentage] = useState(0);
  const [eventPreferences, setEventPreferences] = useState<
    UpdateEventPreferenceMutationVariables["input"]
  >({
    event_id: event.uuid,
    live_hall: false,
    private: false,
    reservation: false,
    asoebi_selection: false,
    dress_code: false,
  });
  const getToken = () => {
    let data = getAccessToken();
    if (data) {
      setToken(data);
    }
  };

  useEffect(() => {
    let wallet_code = localStorage.getItem("walletCode");
    if (wallet_code && wallet_code !== "undefined") {
      setWalletCode(JSON.parse(wallet_code));
    }
  }, []);

  useEffect(() => {
    if (userRole) {
      localStorage.setItem("userRole", userRole);
    }
  }, [userRole]);

  useEffect(() => {
    if (acceptTeamToken) {
      sessionStorage.setItem(ACCEPT_TEAM_TOKEN_SESSION_KEY, acceptTeamToken);
      return;
    }

    sessionStorage.removeItem(ACCEPT_TEAM_TOKEN_SESSION_KEY);
  }, [acceptTeamToken]);

  useEffect(() => {
    if (acceptTeamPhone) {
      sessionStorage.setItem(ACCEPT_TEAM_PHONE_SESSION_KEY, acceptTeamPhone);
      return;
    }

    sessionStorage.removeItem(ACCEPT_TEAM_PHONE_SESSION_KEY);
  }, [acceptTeamPhone]);

  useEffect(() => getToken, []);

  return (
    <AppContext.Provider
      value={{
        token,
        acceptTeamToken,
        setAcceptTeamToken,
        acceptTeamPhone,
        setAcceptTeamPhone,
        setToken,
        setWalletCode,
        walletCode,
        isAdmin,
        setIsAdmin,
        userRole,
        setUserRole,
        eventPreferences,
        setEventPreferences,
        eventName,
        setEventName,
        userId,
        setUserId,
        isLuxeEvent,
        setIsLuxeEvent,
        isLegacyEvent,
        setIsLegacyEvent,
        isPaymentSetup,
        setIsPaymentSetup,
        isPaymentSetupLoading,
        setIsPaymentSetupLoading,
        addTeam,
        setAddTeam,
        asoebiFeePercentage,
        setAsoebiFeePercentage,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
